import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarDays,
  CheckCircle2,
  Inbox,
  MapPin,
  MessageSquare,
  Paperclip,
  Plus,
  RotateCw,
  Tag,
  UserRound,
  X,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  useAddIssueComment,
  useCreateIssue,
  useIssue,
  useIssueCategories,
  useIssues,
} from "@/api/issues";
import {
  ISSUES_PAGE_SIZE,
  ISSUE_PRIORITIES,
  ISSUE_STATUSES,
  Issue,
  IssueListItem,
  IssuePriority,
  IssueStatus,
} from "@/types/issue";
import {
  ApiError,
  AttachmentGrid,
  CategoryPicker,
  CategoryWell,
  CommentThread,
  EmptyState,
  FileDrop,
  IssueCardShell,
  IssueListSkeleton,
  MetaList,
  Pager,
  PriorityChip,
  QuietChip,
  SectionHeading,
  StatusChip,
  StatusTimeline,
} from "@/components/issues/IssueBits"
import { useAttachments } from "@/lib/issueAttachments"
import ResponsiveModal from "@/components/common/ResponsiveModal";
import { useIssueLabels, useRelativeAge, type IssueLabels } from "@/lib/issueLabels";
import { BRAND, PRIORITY_TONE, STATUS_ICON, STATUS_TONE, isSettled } from "@/lib/issueStyles";

type Flash = { type: "success" | "neutral" | "error"; message: string };
type RangeKey = "1m" | "3m" | "all";

const DAY = 24 * 60 * 60 * 1000;

function isWithinRange(dateStr: string, range: RangeKey, now = new Date()) {
  if (range === "all") return true;
  const d = new Date(dateStr);
  if (range === "1m") return d >= new Date(now.getTime() - 30 * DAY);
  return d >= new Date(now.getTime() - 90 * DAY);
}

/* ================================================================== */
export default function IssuesPage() {
  const navigate = useNavigate();
  const location = useLocation() as { pathname: string; state?: { flash?: Flash } };
  const { t } = useLanguage();
  const labels = useIssueLabels();

  const [range, setRange] = React.useState<RangeKey>("1m");
  const [status, setStatus] = React.useState<"ALL" | IssueStatus>("ALL");
  const [page, setPage] = React.useState(1);
  const [flash, setFlash] = React.useState<Flash | null>(location?.state?.flash ?? null);

  // ?new=1 is what /resident/issues/new redirects to. The old route was a
  // full page and may still be bookmarked or linked from an email, so it
  // keeps working and lands here with the sheet already open.
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("issue");
  function setSelectedId(id: string | null) {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("issue", id);
    else next.delete("issue");
    setSearchParams(next, { replace: !id });
  }
  const [reporting, setReporting] = React.useState(searchParams.get("new") === "1");

  React.useEffect(() => {
    if (searchParams.get("new") !== "1") return;
    setReporting(true);
    // Consumed, so a refresh does not reopen it.
    const next = new URLSearchParams(searchParams);
    next.delete("new");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  React.useEffect(() => {
    if (location?.state?.flash) navigate(location.pathname, { replace: true });
  }, [location, navigate]);

  // A success banner that never leaves becomes furniture.
  React.useEffect(() => {
    if (flash?.type !== "success") return;
    const id = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(id);
  }, [flash]);

  React.useEffect(() => {
    setPage(1);
  }, [status, range]);

  const issues = useIssues({ status, page });

  // The date range stays client-side: the API has no date filter, and three
  // fixed buckets do not justify one. It therefore narrows within a page,
  // which is why the pager counts server rows and this does not pretend to.
  const visible = React.useMemo(
    () => (issues.data?.results ?? []).filter((i) => isWithinRange(i.created_at, range)),
    [issues.data, range]
  );

  const total = issues.data?.count ?? 0;

  // Counted from the page in hand. The exact figure lives on the server and
  // the resident portal has no stats endpoint of its own, so this describes
  // what is on screen rather than claiming to describe everything.
  const summary = React.useMemo(() => {
    const all = issues.data?.results ?? [];
    return {
      open: all.filter((i) => !isSettled(i.status)).length,
      resolved: all.filter((i) => i.status === "RESOLVED").length,
    };
  }, [issues.data]);

  return (
    <div className="relative">
      <div
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(900px_500px_at_50%_-10%,rgba(31,58,147,0.07),transparent_60%)]"
        aria-hidden
      />

      {/* pb leaves room for the FAB and the bottom dock. */}
      <div className="space-y-4 pb-32">
        {/* ---------- Header ---------- */}
        <header className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-gray-900">
              {t("issues.myIssues")}
            </h1>
            <p className="mt-0.5 text-[13px] text-gray-600">
              {issues.isLoading
                ? t("common.loading")
                : t("issues.summaryLine", {
                    open: summary.open,
                    resolved: summary.resolved,
                  })}
            </p>
          </div>
          <button
            onClick={() => issues.refetch()}
            disabled={issues.isFetching}
            aria-label={t("common.refresh")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white/80 text-gray-600 ring-1 ring-black/5 backdrop-blur transition hover:bg-white disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93]"
          >
            <RotateCw className={`h-4 w-4 ${issues.isFetching ? "animate-spin" : ""}`} />
          </button>
        </header>

        {flash && <FlashBanner flash={flash} onClose={() => setFlash(null)} />}

        {/* ---------- Status filter ---------- */}
        <div className="-mx-4 overflow-x-auto px-4">
          <div className="inline-flex min-w-full gap-1 rounded-2xl bg-white/70 p-1 ring-1 ring-black/5 backdrop-blur">
            {(["ALL", ...ISSUE_STATUSES] as const).map((key) => {
              const active = status === key;
              const tone = key === "ALL" ? null : STATUS_TONE[key as IssueStatus];
              return (
                <button
                  key={key}
                  onClick={() => setStatus(key)}
                  aria-pressed={active}
                  className={`flex-1 shrink-0 whitespace-nowrap rounded-xl px-3 py-2 text-[13px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                    active ? "shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                  style={
                    active
                      ? tone
                        ? { background: tone.bg, color: tone.fg }
                        : { background: "#FFFFFF", color: "#111827" }
                      : undefined
                  }
                >
                  {key === "ALL" ? t("common.all") : labels.status[key as IssueStatus]}
                </button>
              );
            })}
          </div>
        </div>

        {/* ---------- Range ---------- */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {(["1m", "3m", "all"] as const).map((r) => {
              const active = range === r;
              return (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  aria-pressed={active}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                    active
                      ? "bg-[#1F3A93] text-white shadow-sm"
                      : "bg-white/70 text-gray-600 ring-1 ring-black/5 hover:bg-white"
                  }`}
                >
                  {r === "1m"
                    ? t("issues.range.month")
                    : r === "3m"
                    ? t("issues.range.quarter")
                    : t("issues.range.all")}
                </button>
              );
            })}
          </div>
          {!issues.isLoading && (
            <span className="shrink-0 text-[11px] tabular-nums text-gray-500">
              {t("issues.count", { count: total })}
            </span>
          )}
        </div>

        {/* ---------- List ---------- */}
        {issues.isLoading ? (
          <IssueListSkeleton />
        ) : issues.isError ? (
          <EmptyState
            tone="error"
            icon={AlertTriangle}
            title={t("issues.loadError")}
            action={
              <button
                onClick={() => issues.refetch()}
                className="rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-50"
              >
                {t("common.refresh")}
              </button>
            }
          />
        ) : visible.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title={t("issues.noneMatch")}
            hint={t("issues.emptyHint")}
            action={
              <button
                onClick={() => setReporting(true)}
                className="inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold text-white shadow-sm"
                style={{ background: BRAND }}
              >
                <Plus className="h-3.5 w-3.5" />
                {t("issues.reportIssue")}
              </button>
            }
          />
        ) : (
          <>
            <ul className="space-y-2.5">
              {visible.map((i) => (
                <ResidentIssueCard
                  key={i.id}
                  issue={i}
                  labels={labels}
                  onOpen={() => setSelectedId(i.id)}
                />
              ))}
            </ul>
            <Pager
              page={page}
              pageSize={ISSUES_PAGE_SIZE}
              count={total}
              onChange={setPage}
              busy={issues.isFetching}
            />
          </>
        )}
      </div>

      {/* ---------- FAB ---------- *
          Sits above the bottom dock and clears the iOS home indicator. */}
      <button
        onClick={() => setReporting(true)}
        title={t("issues.reportIssue")}
        aria-label={t("issues.reportIssue")}
        className="fixed right-4 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] z-30 grid h-14 w-14 place-items-center rounded-full text-white shadow-xl shadow-black/20 transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:bottom-[calc(4.5rem+env(safe-area-inset-bottom))]"
        style={{ background: `linear-gradient(140deg, ${BRAND} 0%, ${BRAND} 55%, #27AE60 165%)` }}
      >
        <Plus className="h-6 w-6" />
      </button>

      {reporting && (
        <ReportIssueSheet
          labels={labels}
          onClose={() => setReporting(false)}
          onReported={() => {
            setReporting(false);
            setFlash({ type: "success", message: t("issues.reportSubmitted") });
          }}
        />
      )}

      <IssueDetailsSheet
        key={selectedId ?? "closed"}
        issueId={selectedId}
        labels={labels}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}

/* ================================================================== */
function FlashBanner({ flash, onClose }: { flash: Flash; onClose: () => void }) {
  const { t } = useLanguage();
  const tone =
    flash.type === "success"
      ? { bg: "#DCF5E6", fg: "#12693C", ring: "#A7E0BF", Icon: CheckCircle2 }
      : flash.type === "error"
      ? { bg: "#FEE2E2", fg: "#991B1B", ring: "#FCA5A5", Icon: AlertTriangle }
      : { bg: "#FFFFFF", fg: "#374151", ring: "#E5E7EB", Icon: Inbox };

  return (
    <div
      role="status"
      className="flex items-start gap-2.5 rounded-2xl px-3.5 py-3 text-sm"
      style={{ background: tone.bg, color: tone.fg, boxShadow: `inset 0 0 0 1px ${tone.ring}` }}
    >
      <tone.Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1 leading-snug">{flash.message}</span>
      <button
        onClick={onClose}
        aria-label={t("common.dismiss")}
        className="-mr-1 grid h-6 w-6 shrink-0 place-items-center rounded-full transition hover:bg-black/5"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/* ================================================================== */
function ResidentIssueCard({
  issue,
  labels,
  onOpen,
}: {
  issue: IssueListItem;
  labels: IssueLabels;
  onOpen: () => void;
}) {
  const { t } = useLanguage();
  const settled = isSettled(issue.status);

  return (
    <li>
      <IssueCardShell status={issue.status} settled={settled}>
      <button
        onClick={onOpen}
        title={t("issues.viewDetails")}
        className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#1F3A93]"
      >
        <div className="flex items-start gap-3 px-3.5 py-3.5">
          <CategoryWell icon={issue.category_icon} status={issue.status} />

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 text-[15px] font-semibold leading-snug text-gray-900 line-clamp-2">
                {issue.title}
              </h3>
              <StatusChip status={issue.status} labels={labels} size="sm" />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <PriorityChip priority={issue.priority} labels={labels} />
              <span className="text-gray-300" aria-hidden>·</span>
              <span className="text-[11px] text-gray-500">{issue.category_name ?? "—"}</span>
              <QuietChip>{issue.unit_code ?? t("issues.commonArea")}</QuietChip>
              {issue.attachment_count > 0 && (
                <QuietChip icon={Paperclip}>{issue.attachment_count}</QuietChip>
              )}
              {issue.comment_count > 0 && (
                <QuietChip icon={MessageSquare}>{issue.comment_count}</QuietChip>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
              <span>{new Date(issue.created_at).toLocaleDateString()}</span>
              <span>
                {t("issues.assignedTo")}: {issue.assignee_name ?? t("issues.unassigned")}
              </span>
            </div>
          </div>
        </div>
      </button>
      </IssueCardShell>
    </li>
  );
}

/* ================================================================== */
function IssueDetailsSheet({
  issueId,
  labels,
  onClose,
}: {
  issueId: string | null;
  labels: IssueLabels;
  onClose: () => void;
}) {
  const { t } = useLanguage();
  const detail = useIssue(issueId);
  const commentMutation = useAddIssueComment();
  const relativeAge = useRelativeAge();
  const [comment, setComment] = React.useState("");

  const issue: Issue | undefined = detail.data;
  const latest = issue?.status_changes?.[issue.status_changes.length - 1];
  const StatusGlyph = issue ? STATUS_ICON[issue.status] : null;

  return (
    <ResponsiveModal
      open={!!issueId}
      onClose={onClose}
      headerAccent={issue ? STATUS_TONE[issue.status].rail : undefined}
      title={issue?.title ?? t("common.loading")}
      subtitle={
        issue ? (
          <span className="flex flex-wrap items-center gap-2">
            <PriorityChip priority={issue.priority} labels={labels} />
            <span className="text-gray-400">{relativeAge(issue.created_at)}</span>
          </span>
        ) : undefined
      }
    >
      {detail.isError ? (
        <ApiError error={detail.error} />
      ) : !issue ? (
        <IssueListSkeleton rows={2} />
      ) : (
        <div className="space-y-5">
          {/* The one question a resident opened this to answer: what is
              happening with my report. It leads, in words, before any grid
              of fields. */}
          <div
            className="flex items-start gap-3 rounded-2xl p-4"
            style={{
              background: STATUS_TONE[issue.status].bg,
              color: STATUS_TONE[issue.status].fg,
              boxShadow: `inset 0 0 0 1px ${STATUS_TONE[issue.status].ring}`,
            }}
          >
            {StatusGlyph && <StatusGlyph className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />}
            <div className="min-w-0">
              <p className="text-[15px] font-semibold leading-snug">
                {labels.status[issue.status]}
              </p>
              <p className="mt-0.5 text-[12px] opacity-80">
                {latest
                  ? t("issues.lastUpdated", {
                      when: new Date(latest.created_at).toLocaleDateString(),
                    })
                  : relativeAge(issue.created_at)}
              </p>
              {latest?.note && (
                <p className="mt-2 rounded-lg bg-white/60 px-2.5 py-1.5 text-[13px] leading-relaxed">
                  {latest.note}
                </p>
              )}
            </div>
          </div>

          <section>
            <SectionHeading>{t("common.description")}</SectionHeading>
            <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-700">
              {issue.description}
            </p>
          </section>

          {issue.attachments.length > 0 && (
            <section>
              <SectionHeading>{t("issues.attachments")}</SectionHeading>
              <AttachmentGrid attachments={issue.attachments} />
            </section>
          )}

          {/* The reason the whole feature exists: what happened, in order. */}
          <StatusTimeline changes={issue.status_changes} labels={labels} />

          {/* Facts last. They are reference, not the answer. */}
          <section className="rounded-2xl bg-gray-50/80 p-3.5 ring-1 ring-gray-200/70">
            <MetaList
              items={[
                {
                  label: t("issues.category"),
                  value: issue.category_name ?? "—",
                  icon: Tag,
                },
                {
                  label: t("common.unit"),
                  value: issue.unit_code ?? t("issues.commonArea"),
                  icon: MapPin,
                },
                {
                  label: t("issues.reported"),
                  value: new Date(issue.created_at).toLocaleDateString(),
                  icon: CalendarDays,
                },
                {
                  label: t("issues.assignee"),
                  value: issue.assignee_name ?? t("issues.unassigned"),
                  icon: UserRound,
                },
              ]}
            />
          </section>

          <CommentThread
            comments={issue.comments}
            value={comment}
            onValue={setComment}
            busy={commentMutation.isPending}
            error={commentMutation.error}
            onSubmit={() =>
              commentMutation.mutate(
                { id: issue.id, body: comment.trim() },
                { onSuccess: () => setComment("") }
              )
            }
          />
        </div>
      )}
    </ResponsiveModal>
  );
}


/* ================================================================== *
 * Reporting
 *
 * Was a full route at /resident/issues/new. A separate page for a form the
 * resident reaches from a floating button meant losing the list behind it,
 * a back-navigation to return, and a second implementation of the category
 * grid, the priority control and the upload well -- none of which matched
 * the admin's.
 * ================================================================== */
function ReportIssueSheet({
  labels,
  onClose,
  onReported,
}: {
  labels: IssueLabels;
  onClose: () => void;
  onReported: () => void;
}) {
  const { t } = useLanguage();
  const createIssue = useCreateIssue();

  const [title, setTitle] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [priority, setPriority] = React.useState<IssuePriority>("MEDIUM");
  const [categoryId, setCategoryId] = React.useState<string | null>(null);
  const attachments = useAttachments();

  const categories = useIssueCategories();
  const activeCategories = React.useMemo(
    () => (categories.data ?? []).filter((c) => c.is_active),
    [categories.data]
  );

  React.useEffect(() => {
    if (categoryId || activeCategories.length === 0) return;
    const general = activeCategories.find((c) => c.key === "GENERAL");
    setCategoryId((general ?? activeCategories[0]).id);
  }, [activeCategories, categoryId]);

  const titleOk = title.trim().length >= 3;
  const descOk = description.trim().length >= 10;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!titleOk || !descOk) return;
    createIssue.mutate(
      {
        title: title.trim(),
        description: description.trim(),
        category: categoryId,
        priority,
        attachments: attachments.files,
      },
      // Only on success. The page this replaced navigated away
      // unconditionally, so a rejected upload told the resident their report
      // was filed when nothing had been.
      { onSuccess: onReported }
    );
  }

  return (
    <ResponsiveModal
      open
      onClose={onClose}
      title={t("issues.reportIssue")}
      headerAccent={BRAND}
      size="lg"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="h-11 flex-1 rounded-xl bg-white text-sm font-medium text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="report-issue"
            disabled={createIssue.isPending || !titleOk || !descOk}
            className="h-11 flex-[2] rounded-xl text-sm font-semibold text-white shadow-sm transition active:scale-[0.99] disabled:opacity-50"
            style={{
              background: `linear-gradient(100deg, ${BRAND} 0%, ${BRAND} 55%, #27AE60 160%)`,
            }}
          >
            {createIssue.isPending ? t("common.saving") : t("issues.submitIssue")}
          </button>
        </div>
      }
    >
      <form id="report-issue" onSubmit={submit} className="space-y-4">
        <Field label={t("issues.issueTitle")} required>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("issues.titlePlaceholder")}
            className={inputClass}
          />
        </Field>

        <Field label={t("issues.detailDescription")} required>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={5}
            placeholder={t("issues.detailPlaceholder")}
            className={`${inputClass} min-h-[7rem] resize-y py-2.5`}
          />
          <p className="mt-1 text-[11px] text-gray-500">{t("issues.descriptionHint")}</p>
        </Field>

        {/* No create tile: what the building's categories are is the admin's
            call. A resident whose problem is not listed picks the catch-all
            and explains in the description. */}
        <Field label={t("issues.category")}>
          <CategoryPicker
            categories={activeCategories}
            value={categoryId}
            onChange={setCategoryId}
            loading={categories.isLoading}
          />
        </Field>

        <Field label={t("issues.priority")}>
          <div className="flex gap-1 rounded-xl bg-gray-100/80 p-1">
            {ISSUE_PRIORITIES.map((p) => {
              const active = priority === p;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPriority(p)}
                  aria-pressed={active}
                  className={`flex-1 rounded-lg py-2 text-[12px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1F3A93] ${
                    active ? "shadow-sm" : "text-gray-600 hover:text-gray-900"
                  }`}
                  style={
                    active
                      ? { background: PRIORITY_TONE[p].bg, color: PRIORITY_TONE[p].fg }
                      : undefined
                  }
                >
                  {labels.priority[p]}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label={t("issues.photosOptional")}>
          <FileDrop
            files={attachments.files}
            error={attachments.error}
            onAdd={attachments.add}
            onRemove={attachments.remove}
          />
        </Field>

        <ApiError error={createIssue.error} />
      </form>
    </ResponsiveModal>
  );
}

const inputClass =
  "h-11 w-full rounded-xl bg-white px-3 text-[15px] text-gray-900 ring-1 ring-gray-200 transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1F3A93]";

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </span>
      {children}
    </div>
  );
}
